import { defineStore } from 'pinia'
import { ref,shallowRef, computed, markRaw } from 'vue'
import { rgbToHex } from '../utils/colorUtils'
import { usePreviewStore } from './previewStore'
import { useI18n } from 'vue-i18n'

export interface ColorPoint {
  id: number
  r: number
  g: number
  b: number
  hex: string
  name?: string
}

export interface ArtStyle {
  id: string
  name: string
  description: string
  colors: Array<{ r: number; g: number; b: number; hex: string; name: string }>
  recommendedAlgorithm: 'rgb' | 'lab' | 'weighted-rgb' | 'hsv' | 'ciede2000'
}

export interface PixelGrid {
  width: number
  height: number
  pixels: Array<Array<{ r: number; g: number; b: number; hex: string;id?: number; name?: string; }>>
}

export const useColorStore = defineStore('color', () => {
  const { t } = useI18n()
  
  // 状态
  const uploadedImage = ref<File | null>(null)
  const imageUrl = ref<string>('')
  const gridSize = ref({ width: 200, height: 200 })
  const customColors = shallowRef<ColorPoint[]>([])
  const extractedColors = shallowRef<PixelGrid | null>(null)
  const previewExtractedColors = shallowRef<PixelGrid | null>(null)
  const previewImage = ref<string>('')
  const previewPixelSize = ref<number>(4)
  const colorMatchingAlgorithm = ref<'rgb' | 'lab' | 'weighted-rgb' | 'hsv' | 'ciede2000'>('lab')
  const currentArtStyle = ref<string>('')
  const currentBrand = ref<string>('')
  const brandDisclaimer = ref<string>('')
  const isExtracting = ref<boolean>(false)
  const isRendering = ref<boolean>(false)
  const renderProgress = ref<number>(0)
  const nextId = ref<number>(1) // 添加ID计数器
  // 新增：删除历史（仅记录手动删除）
  const deletedColors = ref<ColorPoint[]>([])
  // 颜色归并设置
  const mergeMode = ref<'rgb' | 'cielab'>('rgb')
  const mergeThreshold = ref(2) // 默认RGB阈值2
  
  // 颜色归并缓存
  const mergeCache = new Map<string, {
    mergedColors: ColorPoint[]
    colorMap: Map<string, ColorPoint>
    timestamp: number
  }>()

  // 生成缓存键
  // function generateMergeCacheKey(mode: 'rgb' | 'cielab', threshold: number): string {
  //   return `${mode}-${threshold}-${customColors.value.length}`
  // }



  // 底色（绣布）设置 - 默认白色
  const baseFabricColor = ref<{ r: number; g: number; b: number; hex: string } | null>({ r: 255, g: 255, b: 255, hex: '#FFFFFF' })

  // 预设画风数据（原始数据，不包含国际化）
  const artStylesData: ArtStyle[] = [
    {
      id: 'ink-wash',
      name: '水墨画',
      description: '传统中国水墨画风格，黑白灰调为主',
      colors: [
        { r: 0, g: 0, b: 0, hex: '#000000', name: '墨黑' },
        { r: 255, g: 255, b: 255, hex: '#FFFFFF', name: '宣纸白' },
        { r: 128, g: 128, b: 128, hex: '#808080', name: '中灰' },
        { r: 64, g: 64, b: 64, hex: '#404040', name: '深灰' },
        { r: 192, g: 192, b: 192, hex: '#C0C0C0', name: '浅灰' },
        { r: 32, g: 32, b: 32, hex: '#202020', name: '浓墨' },
        { r: 160, g: 160, b: 160, hex: '#A0A0A0', name: '淡墨' },
        { r: 96, g: 96, b: 96, hex: '#606060', name: '重墨' }
      ],
      recommendedAlgorithm: 'hsv'
    },
    {
      id: 'black-white',
      name: '黑白画',
      description: '经典黑白对比，强烈的视觉冲击',
      colors: [
        { r: 0, g: 0, b: 0, hex: '#000000', name: '纯黑' },
        { r: 255, g: 255, b: 255, hex: '#FFFFFF', name: '纯白' },
        { r: 128, g: 128, b: 128, hex: '#808080', name: '中灰' },
        { r: 64, g: 64, b: 64, hex: '#404040', name: '深灰' },
        { r: 192, g: 192, b: 192, hex: '#C0C0C0', name: '浅灰' },
        { r: 32, g: 32, b: 32, hex: '#202020', name: '重黑' },
        { r: 160, g: 160, b: 160, hex: '#A0A0A0', name: '轻灰' },
        { r: 96, g: 96, b: 96, hex: '#606060', name: '中深灰' }
      ],
      recommendedAlgorithm: 'hsv'
    },
    {
      id: 'retro',
      name: '复古画',
      description: '怀旧暖色调，营造复古氛围',
      colors: [
        { r: 139, g: 69, b: 19, hex: '#8B4513', name: '马鞍棕' },
        { r: 160, g: 82, b: 45, hex: '#A0522D', name: '赭石色' },
        { r: 205, g: 133, b: 63, hex: '#CD853F', name: '秘鲁色' },
        { r: 244, g: 164, b: 96, hex: '#F4A460', name: '沙褐色' },
        { r: 255, g: 228, b: 196, hex: '#FFE4C4', name: '莫卡辛色' },
        { r: 255, g: 218, b: 185, hex: '#FFDAB9', name: '桃色' },
        { r: 255, g: 192, b: 203, hex: '#FFC0CB', name: '粉红' },
        { r: 255, g: 160, b: 122, hex: '#FFA07A', name: '浅鲑鱼色' }
      ],
      recommendedAlgorithm: 'lab'
    },
    {
      id: 'cartoon',
      name: '卡通画',
      description: '鲜艳明亮色彩，充满活力',
      colors: [
        { r: 255, g: 0, b: 0, hex: '#FF0000', name: '鲜红' },
        { r: 0, g: 255, b: 0, hex: '#00FF00', name: '鲜绿' },
        { r: 0, g: 0, b: 255, hex: '#0000FF', name: '鲜蓝' },
        { r: 255, g: 255, b: 0, hex: '#FFFF00', name: '鲜黄' },
        { r: 255, g: 0, b: 255, hex: '#FF00FF', name: '鲜紫' },
        { r: 0, g: 255, b: 255, hex: '#00FFFF', name: '鲜青' },
        { r: 255, g: 165, b: 0, hex: '#FFA500', name: '鲜橙' },
        { r: 255, g: 20, b: 147, hex: '#FF1493', name: '深粉红' }
      ],
      recommendedAlgorithm: 'rgb'
    },
    {
      id: 'oil-painting',
      name: '油画',
      description: '浓郁艺术色彩，层次丰富',
      colors: [
        { r: 139, g: 0, b: 0, hex: '#8B0000', name: '深红' },
        { r: 0, g: 100, b: 0, hex: '#006400', name: '深绿' },
        { r: 0, g: 0, b: 139, hex: '#00008B', name: '深蓝' },
        { r: 184, g: 134, b: 11, hex: '#B8860B', name: '深金黄' },
        { r: 128, g: 0, b: 128, hex: '#800080', name: '深紫' },
        { r: 85, g: 107, b: 47, hex: '#556B2F', name: '橄榄绿' },
        { r: 139, g: 69, b: 19, hex: '#8B4513', name: '马鞍棕' },
        { r: 47, g: 79, b: 79, hex: '#2F4F4F', name: '深青灰' }
      ],
      recommendedAlgorithm: 'ciede2000'
    },
    {
      id: 'watercolor',
      name: '水彩画',
      description: '清新淡雅色调，自然过渡（加强版）',
      colors: [
        { r: 240, g: 248, b: 255, hex: '#F0F8FF', name: '爱丽丝蓝' },
        { r: 173, g: 216, b: 230, hex: '#ADD8E6', name: '浅蓝' },
        { r: 250, g: 218, b: 221, hex: '#FADADD', name: '淡粉' },
        { r: 255, g: 228, b: 196, hex: '#FFE4C4', name: '米杏' },
        { r: 144, g: 238, b: 144, hex: '#90EE90', name: '浅绿' },
        { r: 221, g: 160, b: 221, hex: '#DDA0DD', name: '李子紫' },
        { r: 255, g: 182, b: 193, hex: '#FFB6C1', name: '浅粉红' },
        { r: 135, g: 206, b: 235, hex: '#87CEEB', name: '天蓝' }
      ],
      recommendedAlgorithm: 'weighted-rgb'
    },
    {
      id: 'cyberpunk',
      name: '赛博朋克',
      description: '高对比霓虹色与深色背景的强烈反差',
      colors: [
        { r: 255, g: 0, b: 102, hex: '#FF0066', name: '霓虹洋红' },
        { r: 0, g: 255, b: 255, hex: '#00FFFF', name: '霓虹青' },
        { r: 255, g: 255, b: 0, hex: '#FFFF00', name: '霓虹黄' },
        { r: 10, g: 10, b: 16, hex: '#0A0A10', name: '夜幕黑' },
        { r: 102, g: 0, b: 204, hex: '#6600CC', name: '深紫' },
        { r: 57, g: 255, b: 20, hex: '#39FF14', name: '霓虹绿' },
        { r: 255, g: 20, b: 147, hex: '#FF1493', name: '亮粉' },
        { r: 0, g: 120, b: 255, hex: '#0078FF', name: '电蓝' }
      ],
      recommendedAlgorithm: 'rgb'
    },
    {
      id: 'vaporwave',
      name: '蒸汽波',
      description: '粉蓝紫为主的复古潮流色',
      colors: [
        { r: 255, g: 182, b: 193, hex: '#FFB6C1', name: '浅粉' },
        { r: 173, g: 216, b: 230, hex: '#ADD8E6', name: '浅蓝' },
        { r: 221, g: 160, b: 221, hex: '#DDA0DD', name: '李子紫' },
        { r: 0, g: 206, b: 209, hex: '#00CED1', name: '浅青' },
        { r: 255, g: 160, b: 122, hex: '#FFA07A', name: '浅橙' },
        { r: 152, g: 251, b: 152, hex: '#98FB98', name: '薄荷绿' },
        { r: 216, g: 191, b: 216, hex: '#D8BFD8', name: '薰衣草紫' },
        { r: 135, g: 206, b: 235, hex: '#87CEEB', name: '天蓝' }
      ],
      recommendedAlgorithm: 'weighted-rgb'
    },
    {
      id: 'pastel',
      name: '粉彩',
      description: '柔和低饱和度的细腻过渡',
      colors: [
        { r: 255, g: 228, b: 225, hex: '#FFE4E1', name: '雾粉' },
        { r: 240, g: 255, b: 240, hex: '#F0FFF0', name: '薄荷白' },
        { r: 240, g: 255, b: 255, hex: '#F0FFFF', name: '冰蓝' },
        { r: 255, g: 250, b: 240, hex: '#FFFAF0', name: '米白' },
        { r: 250, g: 235, b: 215, hex: '#FAEBD7', name: '古董白' },
        { r: 230, g: 230, b: 250, hex: '#E6E6FA', name: '薰衣草' },
        { r: 255, g: 239, b: 213, hex: '#FFEFD5', name: '番木瓜' },
        { r: 224, g: 255, b: 255, hex: '#E0FFFF', name: '淡青' }
      ],
      recommendedAlgorithm: 'weighted-rgb'
    },
    {
      id: 'warm-mono',
      name: '暖色单色',
      description: '以橙棕为主的暖色阶',
      colors: [
        { r: 255, g: 244, b: 214, hex: '#FFF4D6', name: '暖白' },
        { r: 255, g: 219, b: 172, hex: '#FFDBAC', name: '浅杏' },
        { r: 255, g: 191, b: 128, hex: '#FFBF80', name: '浅橙' },
        { r: 255, g: 153, b: 85, hex: '#FF9955', name: '橘' },
        { r: 210, g: 105, b: 30, hex: '#D2691E', name: '巧克力' },
        { r: 160, g: 82, b: 45, hex: '#A0522D', name: '赭石' },
        { r: 120, g: 66, b: 18, hex: '#784212', name: '深棕' },
        { r: 79, g: 36, b: 7, hex: '#4F2407', name: '焦糖' }
      ],
      recommendedAlgorithm: 'lab'
    },
    {
      id: 'cool-mono',
      name: '冷色单色',
      description: '以蓝青为主的冷色阶',
      colors: [
        { r: 240, g: 248, b: 255, hex: '#F0F8FF', name: '爱丽丝蓝' },
        { r: 224, g: 238, b: 255, hex: '#E0EEFF', name: '冰蓝' },
        { r: 173, g: 216, b: 230, hex: '#ADD8E6', name: '浅蓝' },
        { r: 135, g: 206, b: 235, hex: '#87CEEB', name: '天蓝' },
        { r: 0, g: 191, b: 255, hex: '#00BFFF', name: '深天蓝' },
        { r: 70, g: 130, b: 180, hex: '#4682B4', name: '钢青' },
        { r: 25, g: 25, b: 112, hex: '#191970', name: '午夜蓝' },
        { r: 0, g: 105, b: 148, hex: '#006994', name: '海蓝' }
      ],
      recommendedAlgorithm: 'lab'
    },
    {
      id: 'sepia',
      name: '复古胶片',
      description: '偏棕黄的复古照片色调',
      colors: [
        { r: 250, g: 240, b: 230, hex: '#FAF0E6', name: '亚麻' },
        { r: 244, g: 164, b: 96, hex: '#F4A460', name: '沙褐' },
        { r: 205, g: 133, b: 63, hex: '#CD853F', name: '秘鲁' },
        { r: 210, g: 180, b: 140, hex: '#D2B48C', name: '棕褐' },
        { r: 160, g: 82, b: 45, hex: '#A0522D', name: '赭石' },
        { r: 139, g: 69, b: 19, hex: '#8B4513', name: '马鞍棕' },
        { r: 112, g: 66, b: 20, hex: '#704214', name: '咖啡棕' },
        { r: 66, g: 37, b: 17, hex: '#422511', name: '深咖' }
      ],
      recommendedAlgorithm: 'lab'
    },
    {
      id: 'neon',
      name: '霓虹',
      description: '高饱和度的发光感配色',
      colors: [
        { r: 57, g: 255, b: 20, hex: '#39FF14', name: '荧光绿' },
        { r: 255, g: 0, b: 255, hex: '#FF00FF', name: '洋红' },
        { r: 0, g: 255, b: 255, hex: '#00FFFF', name: '青' },
        { r: 255, g: 255, b: 0, hex: '#FFFF00', name: '黄' },
        { r: 0, g: 128, b: 255, hex: '#0080FF', name: '蓝' },
        { r: 255, g: 51, b: 51, hex: '#FF3333', name: '亮红' },
        { r: 255, g: 102, b: 0, hex: '#FF6600', name: '橙' },
        { r: 204, g: 0, b: 255, hex: '#CC00FF', name: '紫' }
      ],
      recommendedAlgorithm: 'rgb'
    },
    {
      id: 'forest',
      name: '森林',
      description: '自然绿色与木色的协调组合',
      colors: [
        { r: 34, g: 139, b: 34, hex: '#228B22', name: '森林绿' },
        { r: 46, g: 139, b: 87, hex: '#2E8B57', name: '海洋绿' },
        { r: 85, g: 107, b: 47, hex: '#556B2F', name: '橄榄绿' },
        { r: 107, g: 142, b: 35, hex: '#6B8E23', name: '橄榄褐' },
        { r: 160, g: 82, b: 45, hex: '#A0522D', name: '赭石' },
        { r: 139, g: 69, b: 19, hex: '#8B4513', name: '马鞍棕' },
        { r: 205, g: 133, b: 63, hex: '#CD853F', name: '秘鲁' },
        { r: 244, g: 164, b: 96, hex: '#F4A460', name: '沙褐' }
      ],
      recommendedAlgorithm: 'ciede2000'
    },
    {
      id: 'ocean',
      name: '海洋',
      description: '从浅滩到深海的蓝绿过渡',
      colors: [
        { r: 224, g: 255, b: 255, hex: '#E0FFFF', name: '淡青' },
        { r: 175, g: 238, b: 238, hex: '#AFEEEE', name: '苍白绿松石' },
        { r: 127, g: 255, b: 212, hex: '#7FFFD4', name: '绿松石' },
        { r: 64, g: 224, b: 208, hex: '#40E0D0', name: '青绿' },
        { r: 0, g: 206, b: 209, hex: '#00CED1', name: '深青' },
        { r: 70, g: 130, b: 180, hex: '#4682B4', name: '钢青' },
        { r: 0, g: 105, b: 148, hex: '#006994', name: '海蓝' },
        { r: 0, g: 51, b: 102, hex: '#003366', name: '深海蓝' }
      ],
      recommendedAlgorithm: 'weighted-rgb'
    },
    {
      id: 'sunset',
      name: '日落',
      description: '由金黄到紫红的日落色带',
      colors: [
        { r: 255, g: 239, b: 213, hex: '#FFEFD5', name: '晚霞金' },
        { r: 255, g: 204, b: 102, hex: '#FFCC66', name: '金黄' },
        { r: 255, g: 153, b: 51, hex: '#FF9933', name: '橘黄' },
        { r: 255, g: 102, b: 102, hex: '#FF6666', name: '橙红' },
        { r: 255, g: 69, b: 0, hex: '#FF4500', name: '朱红' },
        { r: 199, g: 21, b: 133, hex: '#C71585', name: '绛紫' },
        { r: 148, g: 0, b: 211, hex: '#9400D3', name: '深紫罗兰' },
        { r: 75, g: 0, b: 130, hex: '#4B0082', name: '靛青' }
      ],
      recommendedAlgorithm: 'ciede2000'
    },
    {
      id: 'nes',
      name: 'NES/FC 像素风',
      description: '8-bit 复古主机小色表，适合低分辨率像素化',
      colors: [
        { r: 0, g: 0, b: 0, hex: '#000000', name: '黑' },
        { r: 84, g: 84, b: 84, hex: '#545454', name: '暗灰' },
        { r: 136, g: 136, b: 136, hex: '#888888', name: '中灰' },
        { r: 192, g: 192, b: 192, hex: '#C0C0C0', name: '浅灰' },
        { r: 255, g: 255, b: 255, hex: '#FFFFFF', name: '白' },
        { r: 255, g: 0, b: 0, hex: '#FF0000', name: '红' },
        { r: 255, g: 120, b: 0, hex: '#FF7800', name: '橙' },
        { r: 255, g: 216, b: 0, hex: '#FFD800', name: '黄' },
        { r: 0, g: 168, b: 0, hex: '#00A800', name: '绿' },
        { r: 0, g: 232, b: 216, hex: '#00E8D8', name: '青绿' },
        { r: 0, g: 120, b: 248, hex: '#0078F8', name: '亮蓝' },
        { r: 88, g: 88, b: 248, hex: '#5858F8', name: '靛蓝' },
        { r: 248, g: 120, b: 248, hex: '#F878F8', name: '粉紫' },
        { r: 248, g: 88, b: 152, hex: '#F85898', name: '桃粉' },
        { r: 168, g: 0, b: 32, hex: '#A80020', name: '酒红' },
        { r: 104, g: 56, b: 0, hex: '#683800', name: '棕' }
      ],
      recommendedAlgorithm: 'weighted-rgb'
    }
  ]

  // 获取国际化的画风数据
  function getArtStyles(): ArtStyle[] {
    return artStylesData.map(style => {
      // 将连字符格式转换为驼峰格式 (如: ink-wash -> inkWash)
      const camelCaseId = style.id.replace(/-([a-z])/g, (match, letter) => letter.toUpperCase())
      
      return {
        ...style,
        name: t(`artStyles.${camelCaseId}.name`) || style.name,
        description: t(`artStyles.${camelCaseId}.description`) || style.description,
        colors: style.colors.map(color => ({
          ...color,
          name: t(`colorNames.${color.name}`) || color.name
        }))
      }
    })
  }

  // 计算属性
  const hasImage = computed(() => !!uploadedImage.value)
  const hasCustomColors = computed(() => customColors.value.length > 0)
  const canGeneratePreview = computed(() => {
    if (!hasImage.value || !extractedColors.value) return false
    // 检查网格尺寸是否匹配
    return extractedColors.value.width === gridSize.value.width &&
      extractedColors.value.height === gridSize.value.height
  })

  // 方法
  function setImage(file: File) {
    uploadedImage.value = file
    imageUrl.value = URL.createObjectURL(file)
    extractedColors.value = null
    previewImage.value = ''
  }

  function setGridSize(width: number, height: number) {
    gridSize.value = { width, height }
    // 如果已经有提取的颜色但尺寸不匹配，清空预览但保留提取的颜色
    if (extractedColors.value &&
      (extractedColors.value.width !== width || extractedColors.value.height !== height)) {
      previewImage.value = ''
    }
  }

  function addCustomColor(color: Omit<ColorPoint, 'id'>) {
    const newColor: ColorPoint = {
      ...color,
      id: nextId.value++
    }
    customColors.value.push(newColor)
  }

  function removeCustomColor(id: number) {
    const index = customColors.value.findIndex(c => c.id === id)
    if (index > -1) {
      const removed = customColors.value.splice(index, 1)[0]
      if (removed) {
        deletedColors.value.unshift(removed)
        // 可选：限制历史长度
        if (deletedColors.value.length > 200) deletedColors.value.pop()
      }
    }
    const previewStore = usePreviewStore();
    previewStore.renderCustomPreviewOnly()
  }

  // 获取删除历史
  function getDeletedColors(): ColorPoint[] {
    return deletedColors.value
  }


  // 还原删除的颜色
  function restoreDeletedColor(id: number) {
    const idx = deletedColors.value.findIndex(c => c.id === id)
    if (idx === -1) return
    const color = deletedColors.value.splice(idx, 1)[0]
    // 如果当前已存在同 id，使用新 id
    const conflict = customColors.value.some(c => c.id === color.id)
    const restored: ColorPoint = conflict
      ? { ...color, id: nextId.value++ }
      : color
    customColors.value.push(restored)
  }

  // 清空删除历史
  function clearDeletedHistory() {
    deletedColors.value = []
  }

  // 清除归并缓存
  function clearMergeCache() {
    mergeCache.clear()
    console.log('归并缓存已清除')
  }

  function updateCustomColor(
    id: number,
    data: { r: number; g: number; b: number; hex: string; name?: string }
  ) {
    const index = customColors.value.findIndex(c => c.id === id)
    if (index === -1) return
    const existing = customColors.value[index]
    customColors.value[index] = {
      id: existing.id,
      r: data.r,
      g: data.g,
      b: data.b,
      hex: data.hex,
      name: data.name ?? existing.name
    }
    const previewStore = usePreviewStore();
    previewStore.renderCustomPreviewOnly()
  }

  function clearCustomColors() {
    customColors.value = []
    nextId.value = 1 // 重置ID计数器
    clearMergeCache() // 清除归并缓存
    const previewStore = usePreviewStore();
    previewStore.renderCustomPreviewOnly()
  }

  function setExtractedColors(colors: PixelGrid) {
    extractedColors.value = colors
    // 使用浅拷贝，只复制结构，像素数据使用markRaw避免深度响应
    previewExtractedColors.value = {
      width: colors.width,
      height: colors.height,
      pixels: markRaw(colors.pixels.map(row => row.map(pixel => ({ ...pixel }))))
    }
  }

  function setPreviewImage(dataUrl: string) {
    previewImage.value = dataUrl
  }

  function setPreviewPixelSize(size: number) {
    if (size > 0) previewPixelSize.value = size
  }

  function setExtracting(val: boolean) {
    isExtracting.value = val
  }

  function setRendering(val: boolean) {
    isRendering.value = val
  }

  function setRenderProgress(progress: number) {
    renderProgress.value = progress
  }

  function findClosestColor(targetR: number, targetG: number, targetB: number): ColorPoint | null {
    if (customColors.value.length === 0) return null

    let closestColor = customColors.value[0]
    let minDistance = Number.MAX_VALUE

    for (const color of customColors.value) {
      const distance = Math.sqrt(
        Math.pow(targetR - color.r, 2) +
        Math.pow(targetG - color.g, 2) +
        Math.pow(targetB - color.b, 2)
      )

      if (distance < minDistance) {
        minDistance = distance
        closestColor = color
      }
    }

    return closestColor
  }

  function setColorMatchingAlgorithm(algorithm: 'rgb' | 'lab' | 'weighted-rgb' | 'hsv' | 'ciede2000') {
    colorMatchingAlgorithm.value = algorithm
  }

  function applyArtStyle(styleId: string) {
    const styles = getArtStyles()
    const style = styles.find(s => s.id === styleId)
    if (!style) return

    // 清空当前自定义颜色
    // clearCustomColors() // 使用clearCustomColors来重置ID计数器

    // 批量更新状态，避免多次触发响应
    const newColors: ColorPoint[] = style.colors.map((color) => ({
      ...color,
      id: nextId.value++
    }))
    
    // 使用批量更新
    customColors.value = []
    nextId.value = 1
    customColors.value = newColors
    colorMatchingAlgorithm.value = style.recommendedAlgorithm
    currentArtStyle.value = styleId
    currentBrand.value = ''
    brandDisclaimer.value = ''
  }

  // 加载品牌色卡（例如 DMC）
  async function applyBrandPalette(_brand: 'dmc') {
    try {
      // 读取本地 JSON 文本（注意：文件当前为JS风格键名，先规范化）
      const url = new URL('../assets/palettes/dmc.json', import.meta.url).href
      const res = await fetch(url)
      const raw = await res.text()
      // 将未加引号的键名转为标准 JSON：floss/name/r/g/b
      const fixed = raw
        .replace(/(\{|,)\s*(floss)\s*:/g, '$1"floss":')
        .replace(/(\{|,)\s*(name)\s*:/g, '$1"name":')
        .replace(/(\{|,)\s*(r)\s*:/g, '$1"r":')
        .replace(/(\{|,)\s*(g)\s*:/g, '$1"g":')
        .replace(/(\{|,)\s*(b)\s*:/g, '$1"b":')
      const list: Array<{ floss: string | number; name?: string; r: number; g: number; b: number }> = JSON.parse(fixed)

      // 应用到自定义颜色
      const newColors: ColorPoint[] = list.map((color) => {
        const r = Number(color.r) | 0
        const g = Number(color.g) | 0
        const b = Number(color.b) | 0
        const hex = rgbToHex(r, g, b)
        const code = String(color.floss)
        const nm = color.name || ''
        return {
          r, g, b, hex, name: `DMC ${code}${nm ? ' - ' + nm : ''}`,
          id: nextId.value++
        }
      })
      customColors.value = []
      nextId.value = 1
      customColors.value = newColors
      // 推荐算法与标记
      colorMatchingAlgorithm.value = 'ciede2000'
      currentArtStyle.value = 'brand-dmc'
      currentBrand.value = 'dmc'
      brandDisclaimer.value = '提示：DMC 品牌色卡的 RGB/Hex 为屏幕近似值，仅供设计参考，实际线材会有批次/材质/显示设备差异。'
    } catch (e) {
      console.error('加载DMC色卡失败', e)
      throw e
    }
  }

  // 移除原来的getArtStyles函数，因为现在直接在getArtStyles()中返回

  function getCurrentArtStyle() {
    return currentArtStyle.value
  }

  // 调试方法：检查和重置ID计数器
  function debugResetIds() {
    console.log('当前customColors:', customColors.value)
    console.log('当前nextId:', nextId.value)

    // 重新分配正确的ID
    customColors.value.forEach((color, index) => {
      color.id = index + 1
    })

    // 重置nextId为下一个可用的ID
    nextId.value = customColors.value.length + 1

    console.log('重置后的customColors:', customColors.value)
    console.log('重置后的nextId:', nextId.value)
  }

  function clearAll() {
    uploadedImage.value = null
    imageUrl.value = ''
    extractedColors.value = null
    previewExtractedColors.value = null
    previewImage.value = ''
  }

  // 颜色归并设置方法
  function setMergeMode(mode: 'rgb' | 'cielab') {
    mergeMode.value = mode
  }

  function setMergeThreshold(threshold: number) {
    mergeThreshold.value = threshold
  }

  // 设置/清除 底色
  function setBaseFabricColor(color: { r: number; g: number; b: number; hex: string } | null) {
    baseFabricColor.value = color
  }

  return {
    // 状态
    uploadedImage,
    imageUrl,
    gridSize,
    customColors,
    extractedColors,
    previewExtractedColors,
    previewImage,
    previewPixelSize,
    colorMatchingAlgorithm,
    currentArtStyle,
    currentBrand,
    brandDisclaimer,
    isExtracting,
    isRendering,
    renderProgress,

    // 计算属性
    hasImage,
    hasCustomColors,
    canGeneratePreview,

    // 方法
    setImage,
    setGridSize,
    addCustomColor,
    removeCustomColor,
    updateCustomColor,
    clearCustomColors,
    setExtractedColors,
    setPreviewImage,
    setPreviewPixelSize,
    setExtracting,
    setRendering,
    setRenderProgress,
    findClosestColor,
    setColorMatchingAlgorithm,
    applyArtStyle,
    applyBrandPalette,
    getArtStyles,
    getCurrentArtStyle,
    debugResetIds,
    clearAll,
    // 新增：删除历史 API
    getDeletedColors,
    restoreDeletedColor,
    clearDeletedHistory,

    // 颜色归并设置 API
    mergeMode,
    mergeThreshold,
    setMergeMode,
    setMergeThreshold,
    baseFabricColor,
    setBaseFabricColor,

    // 颜色归并缓存 API
    clearMergeCache
  }
})
